import { useEffect, useState } from 'react';
import { CalendarClock, Loader2, Trash2, Plus } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { turnoPorOmision, faltaParaTurno } from './turnoDeGuardia';
import EstadoVacio from '../ui/EstadoVacio';

/* =====================================================================
   Turnos de guardia · DIRECTRIZ-TICKETS F3
   =====================================================================
   Quién cubre fuera del horario. Un ticket crítico que entra con la mesa
   cerrada le suena por Telegram a quien esté de guardia. La lista la ve
   quien atiende; programar y retirar turnos es de quien administra.
   ===================================================================== */

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function Guardia() {
  const [lista, setLista] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [nuevo, setNuevo] = useState(() => turnoPorOmision());
  const [ocupado, setOcupado] = useState(false);

  function recargar() {
    api.get('/tickets/guardia')
      .then((l) => { setLista(l); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudieron cargar los turnos.'));
  }
  useEffect(recargar, []);
  useEffect(() => {
    api.get('/tickets/tecnicos').then(setTecnicos).catch(() => setTecnicos([]));
  }, []);

  const falta = faltaParaTurno(nuevo, tecnicos);

  async function crear() {
    if (ocupado || falta) return;
    setOcupado(true);
    try {
      await api.post('/tickets/guardia', {
        id_usuario: Number(nuevo.id_usuario),
        desde: new Date(nuevo.desde).toISOString(),
        hasta: new Date(nuevo.hasta).toISOString(),
      });
      setNuevo(turnoPorOmision());
      setAviso('Turno programado.');
      recargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo programar el turno.');
    } finally { setOcupado(false); }
  }

  async function baja(g) {
    setOcupado(true);
    try { await api.post(`/tickets/guardia/${g.id}/baja`, {}); recargar(); }
    catch (e) { setError(e instanceof ErrorApi ? e.message : 'No se pudo retirar el turno.'); }
    finally { setOcupado(false); }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <CalendarClock className="h-6 w-6" aria-hidden="true" /> Turnos de guardia
        </h1>
        <p className="text-gray-600">Quién cubre fuera del horario para los tickets críticos.</p>
        <Ayuda id="guardia.pantalla" />
      </header>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {aviso && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{aviso}</p>}

      <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
        <h2 className="font-medium text-gray-900">Programar un turno</h2>
        <Ayuda id="guardia.turno" />
        <select
          value={nuevo.id_usuario}
          onChange={(e) => setNuevo({ ...nuevo, id_usuario: e.target.value })}
          className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base"
        >
          <option value="">Elige quién está de guardia…</option>
          {tecnicos.map((u) => <option key={u.id} value={u.id}>{u.nombre_completo}</option>)}
        </select>
        <label className="text-sm text-gray-700">Desde
          <input type="datetime-local" value={nuevo.desde}
                 onChange={(e) => setNuevo({ ...nuevo, desde: e.target.value })}
                 className="mt-1 min-h-[48px] w-full rounded-xl border border-gray-300 px-3 text-base" />
        </label>
        <label className="text-sm text-gray-700">Hasta
          <input type="datetime-local" value={nuevo.hasta}
                 onChange={(e) => setNuevo({ ...nuevo, hasta: e.target.value })}
                 className="mt-1 min-h-[48px] w-full rounded-xl border border-gray-300 px-3 text-base" />
        </label>
        <button
          type="button" onClick={crear}
          disabled={ocupado || !!falta}
          aria-describedby={falta ? 'motivo-turno-nuevo' : undefined}
          className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-5 w-5" aria-hidden="true" /> Programar turno
        </button>
        {/* P13 (29 sep 2026): el censo lo contaba «gris sin decir por qué». */}
        <Motivo id="motivo-turno-nuevo" cuando={!!falta}>{falta}</Motivo>
      </div>

      {lista === null && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando turnos…
        </p>
      )}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={CalendarClock}
          titulo="Todavía no hay turnos de guardia"
          descripcion="Programa arriba quién cubre fuera del horario. Sin turno, un ticket crítico de noche o en fin de semana no le suena a nadie."
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="flex flex-col gap-2">
          {lista.map((g) => (
            <li key={g.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
              <span className="min-w-0">
                <span className="block truncate font-medium text-gray-900">{g.nombre_completo}</span>
                <span className="block text-xs text-gray-500">{fmt(g.desde)} → {fmt(g.hasta)}</span>
              </span>
              <button type="button" onClick={() => baja(g)} disabled={ocupado}
                      className="flex min-h-[44px] items-center gap-1 rounded-xl border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Retirar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
